import { Page } from "components/page";
import { type GetStaticPaths, type GetStaticProps, type NextPage } from "next";
import { type MDXRemoteSerializeResult } from "next-mdx-remote";
import { serialize } from "next-mdx-remote/serialize";
import rehypeHighlight from "rehype-highlight";
import rehypeSlug from "rehype-slug";
import remarkGfm from "remark-gfm";
import React from "react";
import { LegalPage } from "components/legal";
import { versions, versionHistory } from "data/legal/privacy-policy";

type PageProps = {
  privacySource: MDXRemoteSerializeResult;
  version: string;
  lastUpdatedDate: string;
};

const PrivacyPolicyVersion: NextPage<Readonly<PageProps>> = ({ privacySource, version, lastUpdatedDate }) => {
  const formattedDate = new Date(version).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <Page
      noIndex
      title={`${formattedDate} Privacy Policy`}
      description="At Hightouch, accessible from hightouch.com, one of our main priorities is the privacy of our visitors."
      footerOptions={{ hideCta: true }}
    >
      <LegalPage
        title="Privacy Policy"
        lastUpdatedDate={lastUpdatedDate}
        mdxSource={privacySource}
        versionHistory={versionHistory}
        pageVersion={version}
        currentVersionSlug="privacy-policy"
      />
    </Page>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  // Generate paths for all versions except the current one
  const paths = Object.keys(versions)
    .filter((version) => version !== "current")
    .map((version) => ({
      params: { version },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ params }) => {
  const version = params?.version as string;

  if (!versions[version]) {
    return { notFound: true };
  }

  const versionData = versions[version];
  const privacyText = versionData.content;

  const privacySource = await serialize(privacyText, {
    mdxOptions: {
      rehypePlugins: [rehypeSlug, rehypeHighlight],
      remarkPlugins: [remarkGfm],
      format: "md",
    },
  });

  return {
    props: {
      privacySource,
      version,
      lastUpdatedDate: versionData.date,
    },
  };
};

export default PrivacyPolicyVersion;
